// One Idempotency-Key per console write, held until the server has answered
// it (spec 15 §5, the platform replay store).
//
// A write whose answer never arrived (a dropped connection, a 502 from the
// load balancer, a timeout) may still have committed. Sending it again under a
// NEW key runs it twice: a second create answers 409 SUBDOMAIN_TAKEN for the
// tenant the first one made, and a second suspend answers 409 for the
// suspension it already recorded. Sending it again under the SAME key and the
// same body replays the stored answer instead, or runs it for the first time
// if it never committed (the server releases the key on a 5xx).
//
// So the key is held while the body stays the same and the answer is unclear,
// and replaced when the body changes (the store refuses one key for two
// bodies with IDEMPOTENCY_KEY_REUSE) or once the server has given a definite
// answer.

import { useRef } from "react";
import { newIdempotencyKey } from "../../../lib/request";
import { PlatformError } from "./platform-api";

/**
 * Whether a failed write got a definite answer from the API: a 4xx it decided
 * on. Everything else leaves the outcome unknown, and the next try must reuse
 * the key: a rejection with no response (fetch's TypeError), any 5xx (the ALB's
 * 502 and 504 included), 408 and 429, and IDEMPOTENCY_IN_PROGRESS, which means
 * the first attempt is still running.
 */
export function isDefiniteAnswer(error: unknown): boolean {
  if (!(error instanceof PlatformError)) return false;
  if (error.status < 400 || error.status >= 500) return false;
  if (error.status === 408 || error.status === 429) return false;
  return error.code !== "IDEMPOTENCY_IN_PROGRESS";
}

export class HeldKey {
  private key: string | null = null;
  private body: string | null = null;
  private readonly mint: () => string;
  constructor(mint: () => string = newIdempotencyKey) {
    this.mint = mint;
  }

  /** The key for this body: the held one if the body is unchanged, else a new one. */
  for(body: unknown): string {
    const fingerprint = JSON.stringify(body ?? null);
    if (this.key === null || this.body !== fingerprint) {
      this.key = this.mint();
      this.body = fingerprint;
    }
    return this.key;
  }

  /** Record how the write ended: a success or a definite refusal frees the key. */
  settle(error?: unknown): void {
    if (error === undefined || isDefiniteAnswer(error)) {
      this.key = null;
      this.body = null;
    }
  }
}

/** A `HeldKey` that lives as long as the component that owns the write. */
export function useHeldKey(): HeldKey {
  const ref = useRef<HeldKey | null>(null);
  ref.current ??= new HeldKey();
  return ref.current;
}

/**
 * Send one write through a held key: the key for this body, then settled by
 * the outcome. The caller passes the body it sends, so a changed field means a
 * new key.
 */
export async function withHeldKey<T>(
  held: HeldKey,
  body: unknown,
  send: (key: string) => Promise<T>,
): Promise<T> {
  const key = held.for(body);
  try {
    const result = await send(key);
    held.settle();
    return result;
  } catch (error) {
    held.settle(error);
    throw error;
  }
}
